import LogOutIcon from '@/shared/assets/logout2-icon.svg?react';
import SettingsIcon from '@/shared/assets/settings-icon.svg?react';
import { useTranslation } from 'react-i18next';
import { tv } from 'tailwind-variants';
import { UserLanguageTooltip } from '@/app/layouts/authorized/user-balance-dropdown/ui/UserLanguageTooltip';
import { ChevronRight } from 'lucide-react';
import LanguageIcon from '@/shared/assets/language-icon.svg?react';
import { useAppSettingsStore } from '@/shared/lib';
import { cn } from '@/shared/utils';

const classes = tv({
  slots: {
    menu: 'flex flex-col gap-[0.8rem] transition-all duration-400 px-[1.2rem] pb-[1.2rem]',
    menuItem:
      'w-full h-[4.8rem] flex items-center gap-[1.6rem] rounded-[1.2rem] hover:bg-secondary-bg cursor-pointer text-primary px-[0.8rem]',
    menuItemIconWrapper: 'flex items-center justify-center',
    menuItemIcon: 'size-[2.4rem]',
    menuItemText: 'headline4 flex-1',
    menuItemWarningIcon: 'size-[1.8rem]',
    menuItemLocale: 'flex row p4 items-center gap-[0.8rem] text-secondary',
    chevron: 'size-[2.4rem] text-tertiary',
  },
  variants: {
    open: {
      true: {
        menu: 'max-h-[30rem] opacity-100',
      },
      false: {
        menu: 'max-h-0 opacity-0',
      },
    },
    isLogout: {
      true: {
        menuItem: 'hover:text-destructive',
      },
      false: {
        menuItem: 'hover:text-primary',
      },
    },
  },
  defaultVariants: {
    open: false,
    isLogout: false,
  },
});

type Props = {
  open: boolean;
  tooltipOpen: boolean;
  setTooltipOpen: () => void;
  onOpenProfile: (e: React.MouseEvent) => void;
  onLogout: (e: React.MouseEvent) => void;
  isUserVerified: boolean;
};

export const UserMenu = ({
  open,
  tooltipOpen,
  onOpenProfile,
  setTooltipOpen,
  onLogout,
  isUserVerified,
}: Props) => {
  const { t } = useTranslation();

  const { getLanguage } = useAppSettingsStore();
  const language = getLanguage();

  const c = classes();

  return (
    <div className={c.menu({ open })}>
      <div className={c.menuItem()} onClick={onOpenProfile}>
        <div className={c.menuItemIconWrapper()}>
          <SettingsIcon className={c.menuItemIcon()} />
        </div>
        <span className={c.menuItemText()}>{t('common.settings')}</span>
        {!isUserVerified && (
          <img
            src="/assets/warning-status-icon.svg"
            alt="Warning"
            className={c.menuItemWarningIcon()}
          />
        )}
      </div>

      <UserLanguageTooltip
        tooltipOpen={tooltipOpen}
        setTooltipOpen={setTooltipOpen}>
        <div className={cn(c.menuItem(), tooltipOpen ? 'bg-secondary-bg' : '')}>
          <div className={c.menuItemIconWrapper()}>
            <LanguageIcon className={c.menuItemIcon()} />
          </div>
          <span className={c.menuItemText()}>{t('common.language.title')}</span>
          <span className={c.menuItemLocale()}>
            {t(`common.language.options.${language}`)}{' '}
            <ChevronRight className={c.chevron()} />
          </span>
        </div>
      </UserLanguageTooltip>

      <div className={c.menuItem({ isLogout: true })} onClick={onLogout}>
        <div className={c.menuItemIconWrapper()}>
          <LogOutIcon className={c.menuItemIcon()} />
        </div>
        <span className={c.menuItemText()}>{t('common.logout')}</span>
      </div>
    </div>
  );
};
